<template>
  <div
    :class="[
      $style.authLayout,
      { [$style.isSigningUp]: isSigningUp },
      { [$style.noAnimation]: noAnimation },
    ]"
  >
    <div
      :class="$style.leftBlock"
      :style="{ backgroundImage: `url(${background})` }"
    >
      <slot name="leftBlock" />
    </div>
    <div :class="$style.rightBlock">
      <img
        :class="$style.logo"
        src="@/images/Logotype.svg"
        alt="logo"
        @click="handleLogoClick"
      />
      <div :class="$style.rightContent">
        <slot name="rightBlock" />
      </div>
      <AuthFooter />
    </div>
  </div>
</template>

<script setup>
import { ref, provide, onMounted, watch, defineProps } from "vue";
import background from "@/images/sign-in/background.jpg";
import { INJECT_KEYS } from "@/constants/injectKeys";
import AuthFooter from "@/components/AuthFooter";

const props = defineProps({
  noAnimation: {
    type: Boolean,
    default: false,
  },
});

const handleLogoClick = () => {
  window.location.href = "https://hackenproof.com";
};

// Parse URL query parameters
const getQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  return Object.fromEntries(params.entries());
};

// Update URL query parameter without page reload
const updateQueryParam = (key, value) => {
  const url = new URL(window.location.href);
  if (value === undefined || value === null) {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, value);
  }
  window.history.replaceState({}, "", url.toString());
};

// Initialize from URL
const currentParams = getQueryParams();
const isSigningUp = ref(currentParams.signUp === "true");

// Function to toggle or set signing up state
const toggleSignUp = (value = null) => {
  isSigningUp.value = value !== null ? value : !isSigningUp.value;
};

// Sync URL when isSigningUp changes
watch(isSigningUp, (newValue) => {
  updateQueryParam("signUp", newValue ? "true" : undefined);
});

// Listen for popstate events (back/forward browser navigation)
onMounted(() => {
  // Initial check
  const initialParams = getQueryParams();
  if (initialParams.signUp === "true" && !isSigningUp.value) {
    isSigningUp.value = true;
  } else if (initialParams.signUp !== "true" && isSigningUp.value) {
    isSigningUp.value = false;
  }

  // Listen for browser back/forward navigation
  window.addEventListener("popstate", () => {
    const params = getQueryParams();
    isSigningUp.value = params.signUp === "true";
  });
});

provide(INJECT_KEYS.SIGN_UP_OPEN_DRAWER, isSigningUp);
provide(INJECT_KEYS.TOGGLE_SIGN_UP, toggleSignUp);
</script>

<style module lang="scss">
.authLayout {
  display: flex;
  width: 100vw;
  min-height: 100vh;

  @media screen and (max-width: 990px) {
    height: auto;
  }

  @media screen and (max-width: 760px) {
    flex-direction: column;
  }

  .leftBlock {
    min-height: 100%;
    transition: flex 0.6s ease-in-out;
    background-size: cover;
    background-position: center;
    width: 0%;
    flex: 0 0 710px;

    @media screen and (max-width: 1920px) {
      flex-basis: 500px;
    }

    @media screen and (max-width: 1430px) {
      flex-basis: 420px;
    }

    @media screen and (max-width: 1200px) {
      flex-basis: 350px;
      min-height: 100vh;
    }

    @media screen and (max-width: 990px) {
      flex-basis: 264px;
    }

    @media screen and (max-width: 760px) {
      flex-basis: 0px;
      width: 100%;
      display: none;
    }

    .leftContent {
      padding: 40px 180px 40px 172px;
      width: 710px;
    }
  }

  .rightBlock {
    flex: 1 1;
    width: 0%;
    height: 100%;
    background-color: #fff;
    overflow: hidden;
    transition: flex 0.6s ease-in-out, width 0.6s ease-in-out;
    display: flex;
    flex-direction: column;
    min-height: 100vh;

    @media screen and (max-width: 760px) {
      width: 100%;
    }

    .logo {
      display: none;
      cursor: pointer;

      @media screen and (max-width: 760px) {
        display: block;
        align-self: flex-start;
        margin: 16px 16px 40px;
        width: 117px;
      }
    }

    .rightContent {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: 120px;
      flex: 1 1;

      @media screen and (max-width: 1430px) {
        padding-top: 84px;
      }

      @media screen and (max-width: 980px) {
        padding-top: 64px;
      }

      @media screen and (max-width: 760px) {
        padding-top: 0px;
      }
    }
  }

  &.isSigningUp {
    .leftBlock {
      flex: 1 0;

      @media screen and (max-width: 760px) {
        display: flex;
      }
    }

    .rightBlock {
      flex: 0 1;
      width: 0px;

      @media screen and (max-width: 760px) {
        display: none;
      }
    }
  }

  &.noAnimation {
    .leftBlock {
      transition: none;
    }

    .rightBlock {
      transition: none;
    }
  }
}
</style>
